"use client";

import { useCoarsePointer } from "@/studio/lib/iconButtonTitle";

import { useStudioMenuBlur, studioMenuVeilCard } from "@/studio/lib/studioMenuBlur";
import {
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ArrowDown, ArrowLeft, Check, Ellipsis, X } from "lucide-react";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { StudioPortalLayer } from "@/studio/components/chrome/overlays";
import { placeFloating, type FloatingRect } from "@/studio/lib/floatingPlacement";
import "./studio-action-menu.css";

export type StudioActionIcon = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type StudioActionItem = {
  id: string;
  label: string;
  icon?: StudioActionIcon | ReactNode;
  danger?: boolean;
  disabled?: boolean;
  selected?: boolean;
  /** Items that share a section sit in one background group. No dividers. */
  section?: string;
  onSelect?: () => void;
  /** First click can stay open (confirm-to-delete). Default closes. */
  closeOnSelect?: boolean;
  onContextMenu?: (point: { x: number; y: number }) => void;
  /** Skip the accent icon tile (color dots, Close). */
  plain?: boolean;
  /** Opens a second page. Back returns to the previous page. */
  children?: StudioActionItem[];
  /** Current value shown at the end of the row (e.g. "16:9"). */
  value?: ReactNode;
  /** Picking this row returns to the previous page instead of closing. */
  backOnSelect?: boolean;
  /** Custom inline control (slider, switch) rendered instead of a button row. */
  content?: ReactNode;
  /** User-supplied label (org/account name): cap its width with an ellipsis
   *  so a long name never widens the menu. Full text stays in the tooltip. */
  truncate?: boolean;
  /** Second line under the label (add-step palette, etc.). */
  hint?: string;
  /** Uppercase caption once at the top of this item's section group. */
  sectionHeader?: string;
  /** Separate button at the right end of the row. Opens its own page
   *  (`children`) without selecting the row (e.g. Agent → model picker). */
  accessory?: {
    id: string;
    label: string;
    content: ReactNode;
    children: StudioActionItem[];
  };
  /** First item of a page draws its own Back / Close (no Back row, no sheet head). */
  ownsNav?: boolean;
};

/** Children for a page id: a row's own page, else a row accessory's page. */
function pageChildren(items: StudioActionItem[], id: string): StudioActionItem[] {
  const row = items.find((item) => item.id === id);
  if (row?.children) return row.children;
  return items.find((item) => item.accessory?.id === id)?.accessory?.children ?? [];
}

/** Title for a page id (row label or accessory label). */
function pageLabel(items: StudioActionItem[], id: string): string | null {
  const row = items.find((item) => item.id === id);
  if (row) return row.label;
  return items.find((item) => item.accessory?.id === id)?.accessory?.label ?? null;
}

function renderIcon(icon: StudioActionItem["icon"]) {
  if (icon == null || icon === false || icon === true) return null;
  if (isValidElement(icon)) return icon;
  // Lucide icons are forwardRef objects ({ $$typeof, render }), not functions.
  if (typeof icon === "function" || (typeof icon === "object" && "render" in icon)) {
    const Icon = icon as StudioActionIcon;
    return <Icon className="h-3.5 w-3.5" aria-hidden />;
  }
  return icon;
}

/** A dropdown's own toggle (CursorSelect, filter pills, ...) while its menu is open. */
const OPEN_TOGGLE_SELECTOR = '[aria-haspopup][aria-expanded="true"]';

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Exit slide length; keep in sync with the `*-out` animations in studio-files-chrome.css. */
const MENU_EXIT_MS = 180;

/** Desktop opening render: mounted, measured, not yet placed or painted. */
const MENU_HIDDEN_STYLE: CSSProperties = {
  position: "fixed",
  top: "0px",
  left: "0px",
  visibility: "hidden",
};

/** A scroll that ends over a row must not select it or dismiss the menu.
 *  Scroll-only — no onTouchMove. React touchmove handlers are not passive and
 *  make the list feel delayed. */
const menuGesture = { lastScroll: 0 };

function menuGestureWasScroll() {
  return performance.now() - menuGesture.lastScroll < 350;
}

const menuGestureHandlers = {
  onScroll: () => {
    menuGesture.lastScroll = performance.now();
  },
};

function MenuRow({
  icon,
  label,
  danger,
  disabled,
  selected,
  plain,
  hasChildren,
  submenuCaret,
  value,
  showCheck,
  truncate,
  hint,
  onContextMenu,
  onSelect,
}: {
  icon?: StudioActionItem["icon"];
  label: string;
  hint?: string;
  truncate?: boolean;
  value?: ReactNode;
  /** Trailing check on the selected row (menus that pick one value). */
  showCheck?: boolean;
  danger?: boolean;
  disabled?: boolean;
  selected?: boolean;
  plain?: boolean;
  hasChildren?: boolean;
  /** `dot` = Messages context submenu; default ArrowDown for other menus. */
  submenuCaret?: "dot" | "arrow";
  onContextMenu?: (point: { x: number; y: number }) => void;
  onSelect: () => void;
}) {
  const glyph = renderIcon(icon);
  return (
    <button
      type="button"
      className={`cursor-tab-context-item${danger ? " is-danger" : ""}${selected ? " is-selected" : ""}${plain ? " is-plain" : ""}${hasChildren ? " has-submenu" : ""}${value != null ? " has-value" : ""}${hint ? " has-hint" : ""}`}
      role="menuitem"
      aria-checked={selected || undefined}
      aria-haspopup={hasChildren ? "menu" : undefined}
      disabled={disabled}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={
        onContextMenu
          ? (event) => {
              event.preventDefault();
              event.stopPropagation();
              onContextMenu({ x: event.clientX, y: event.clientY });
            }
          : undefined
      }
      onClick={(event) => {
        event.stopPropagation();
        if (disabled || menuGestureWasScroll()) return;
        onSelect();
      }}
    >
      {glyph ? (
        <span className="cursor-tab-context-item-icon" aria-hidden="true">
          {glyph}
        </span>
      ) : null}
      <span
        className={`cursor-tab-context-item-label${truncate ? " is-truncate" : ""}`}
        title={truncate ? label : hint ? `${label} — ${hint}` : undefined}
      >
        {hint ? (
          <span className="cursor-tab-context-item-stack">
            <span className="cursor-tab-context-item-title">{label}</span>
            <span className="cursor-tab-context-item-hint">{hint}</span>
          </span>
        ) : (
          label
        )}
      </span>
      {value != null ? (
        <span className="studio-action-menu-value">{value}</span>
      ) : null}
      {selected && showCheck ? (
        <Check className="studio-action-menu-check" aria-hidden="true" />
      ) : null}
      {hasChildren ? (
        submenuCaret === "dot" ? (
          <span className="studio-dm-menu-caret" aria-hidden="true" />
        ) : (
          <ArrowDown className="cursor-tab-context-caret" aria-hidden="true" />
        )
      ) : null}
    </button>
  );
}

/** Lets custom row content (inline forms) go back a page or close the menu. */
const StudioActionMenuNavContext = createContext<{
  back: () => void;
  close: () => void;
} | null>(null);

export function useStudioActionMenuNav() {
  return useContext(StudioActionMenuNavContext);
}

function MenuPages({
  items,
  page,
  onOpen,
  onBack,
  onDone,
  submenuCaret,
  showChecks,
}: {
  items: StudioActionItem[];
  page: string[];
  onOpen: (id: string) => void;
  onBack: () => void;
  onDone: () => void;
  submenuCaret?: "dot" | "arrow";
  showChecks?: boolean;
}) {
  let current = items;
  for (const id of page) {
    current = pageChildren(current, id);
  }
  const groups: { id: string; items: StudioActionItem[] }[] = [];
  for (const item of current) {
    const id = item.section ?? "default";
    const last = groups[groups.length - 1];
    if (!last || last.id !== id) groups.push({ id, items: [item] });
    else last.items.push(item);
  }
  return (
    <>
      {page.length > 0 && !current[0]?.ownsNav ? (
        <div className="studio-action-section is-back">
          <MenuRow icon={ArrowLeft} label="Back" onSelect={onBack} />
        </div>
      ) : null}
      {groups.map((group) => (
        <div key={group.id} className="studio-action-section">
          {group.items[0]?.sectionHeader ? (
            <p className="studio-action-section-label" role="presentation">
              {group.items[0].sectionHeader}
            </p>
          ) : null}
          {group.items.map((item) =>
            item.content != null ? (
              <div
                key={item.id}
                className="studio-action-menu-custom"
                role="group"
                aria-label={item.label}
              >
                <StudioActionMenuNavContext.Provider value={{ back: onBack, close: onDone }}>
                  {item.content}
                </StudioActionMenuNavContext.Provider>
              </div>
            ) : item.accessory ? (
              <div key={item.id} className="studio-action-row-with-accessory">
                <MenuRow
                  icon={item.icon}
                  label={item.label}
                  hint={item.hint}
                  danger={item.danger}
                  disabled={item.disabled}
                  selected={item.selected}
                  plain={item.plain}
                  value={item.value}
                  truncate={item.truncate}
                  showCheck={showChecks}
                  onContextMenu={item.onContextMenu}
                  onSelect={() => {
                    item.onSelect?.();
                    if (item.closeOnSelect !== false) onDone();
                  }}
                />
                <button
                  type="button"
                  className="studio-action-row-accessory"
                  aria-label={item.accessory.label}
                  title={item.accessory.label}
                  aria-haspopup="menu"
                  disabled={item.disabled}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (menuGestureWasScroll()) return;
                    onOpen(item.accessory!.id);
                  }}
                >
                  {item.accessory.content}
                </button>
              </div>
            ) : (
            <MenuRow
              key={item.id}
              icon={item.icon}
              label={item.label}
              hint={item.hint}
              danger={item.danger}
              disabled={item.disabled}
              selected={item.selected}
              plain={item.plain}
              value={item.value}
              truncate={item.truncate}
              showCheck={showChecks}
              hasChildren={Boolean(item.children?.length)}
              submenuCaret={submenuCaret}
              onContextMenu={item.onContextMenu}
              onSelect={() => {
                if (item.children?.length) {
                  onOpen(item.id);
                  return;
                }
                item.onSelect?.();
                if (item.backOnSelect && page.length > 0) {
                  onBack();
                  return;
                }
                if (item.closeOnSelect !== false) onDone();
              }}
            />
            ),
          )}
        </div>
      ))}
    </>
  );
}

export function StudioActionMenu({
  label = "Menu",
  items,
  triggerClassName,
  triggerTitle,
  trigger,
  menuClassName = "",
  point,
  placement = "below",
  dock = "right",
  phoneLayout = "edge",
  lead,
  head,
  onClose,
  open: openProp,
  onOpenChange,
  showChecks = false,
  veil = true,
  dismissed = false,
  onCloseStart,
  menuWidth,
  menuMinWidth,
  anchorRect,
  anchorAlign = "start",
}: {
  /**
   * Desktop only: the trigger box of a point menu (e.g. CursorSelect). The
   * menu opens below it, aligns to its end near the right edge, and flips
   * above when there is no room below (see placeFloating).
   */
  anchorRect?: FloatingRect | null;
  /** Preferred horizontal alignment against `anchorRect`. */
  anchorAlign?: "start" | "end";
  /** Desktop only: fixed menu width, e.g. a field select matching its trigger. */
  menuWidth?: number;
  /** Desktop only: the menu is never narrower than this (its trigger). */
  menuMinWidth?: number;
  label?: string;
  /** Show a trailing check on `selected` rows. */
  showChecks?: boolean;
  items: StudioActionItem[];
  triggerClassName?: string;
  /** Tooltip on the trigger (icon-only triggers). Hidden on touch, like StudioIconButton. */
  triggerTitle?: string;
  /** Replaces the default ellipsis button contents. */
  trigger?: ReactNode;
  menuClassName?: string;
  /** Controlled open state. Omit to let the menu own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Right-click position. When set, the menu is already open and has no trigger. */
  point?: { x: number; y: number } | null;
  /** Desktop only. `above` treats `y` as the bottom edge of the menu. */
  placement?: "above" | "below";
  /** Phone only. `left` docks the card to the screen edge, rounded on the right. */
  dock?: "left" | "right";
  /**
   * Phone only. `edge` docks to the side. `clamp` uses the desktop flip so the
   * menu stays inside the visual viewport.
   */
  phoneLayout?: "edge" | "clamp";
  /** Row above the actions (message reactions). */
  lead?: ReactNode;
  /** Second row under the phone title. Title and close stay on the first row. */
  head?: ReactNode;
  onClose?: () => void;
  /** Full-card blur veil via `useStudioMenuBlur` (default on). */
  veil?: boolean;
  /**
   * Point menus only: the parent closed it (its own toggle, phone Back, an item
   * that navigates). The menu plays the same exit slide as a scrim tap; back to
   * false during the slide reopens it. The parent must keep it mounted until
   * `onClose` (see useStudioExitPresence).
   */
  dismissed?: boolean;
  /** Runs when the exit starts (any path); `onClose` of a point menu runs when it ends. */
  onCloseStart?: () => void;
}) {
  const { isMobile } = useMobileLayout();
  const edgeSheet = isMobile && phoneLayout !== "clamp";
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDialogElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (value: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(value);
    onOpenChange?.(value);
  };
  const [page, setPage] = useState<string[]>([]);
  const [menuStyle, setMenuStyle] = useState<Record<string, string> | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [exitHold, setExitHold] = useState(false);
  const leavingRef = useRef(false);
  const ignoreDismissUntilRef = useRef(0);
  /** Mouse press already opened/closed the menu; skip the click that follows. */
  const pressToggledRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onCloseStartRef = useRef(onCloseStart);
  onCloseStartRef.current = onCloseStart;

  const exitTimerRef = useRef(0);
  /** Point menu closing: parent's onClose waits for the slide-out (it unmounts us). */
  const pendingCloseRef = useRef(false);
  const exitWasAnchoredRef = useRef(false);
  const closedPointRef = useRef<{ x: number; y: number } | null>(null);

  // exitHold keeps the menu mounted for the leave slide after `open` is false
  // (or, for a point menu, after a parent cleared `point` itself).
  const anchored = Boolean(point) || (exitHold && exitWasAnchoredRef.current);
  const visible = exitHold || anchored || open;

  const armIgnoreDismiss = () => {
    // Opening under a still-down finger would hit the scrim on pointerup/click.
    ignoreDismissUntilRef.current = performance.now() + 450;
  };
  const shouldIgnoreDismiss = () => performance.now() < ignoreDismissUntilRef.current;

  /** Exit slide finished (animationend) or its timeout fallback fired. */
  const finishExit = () => {
    if (!leavingRef.current) return;
    window.clearTimeout(exitTimerRef.current);
    exitTimerRef.current = 0;
    leavingRef.current = false;
    setLeaving(false);
    setExitHold(false);
    if (pendingCloseRef.current) {
      pendingCloseRef.current = false;
      onCloseRef.current?.();
    }
  };

  /** Reopened during the exit slide: keep the mounted menu, no second open. */
  const cancelExit = () => {
    if (!leavingRef.current) return;
    window.clearTimeout(exitTimerRef.current);
    exitTimerRef.current = 0;
    leavingRef.current = false;
    pendingCloseRef.current = false;
    setLeaving(false);
    setExitHold(false);
  };

  // Close reverses the open: card slides back out to the right, the veil fades
  // to unblurred, and the menu stays mounted until that ends. The item's action
  // already ran before close() (MenuRow calls onSelect first), so nothing waits.
  const close = () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    exitWasAnchoredRef.current = anchored;
    closedPointRef.current = point ? { x: point.x, y: point.y } : null;
    setLeaving(true);
    setExitHold(true);
    if (anchored) {
      // Point menus are unmounted by the parent's onClose; run it after the slide.
      pendingCloseRef.current = true;
    } else {
      setOpen(false);
      onCloseRef.current?.();
    }
    onCloseStartRef.current?.();
    armExitTimer();
  };

  const armExitTimer = () => {
    window.clearTimeout(exitTimerRef.current);
    // Fallback if animationend never fires (hidden tab, reduced motion).
    exitTimerRef.current = window.setTimeout(
      finishExit,
      prefersReducedMotion() ? 0 : MENU_EXIT_MS + 100,
    );
  };

  // A controlled trigger menu closed by its parent (phone Back, a sibling menu
  // opening, a row that navigates) used to unmount on the spot: `visible`
  // dropped with `open`. Hold it for the same exit slide. Adjusted during
  // render so the closing render never unmounts it.
  const [prevOpenProp, setPrevOpenProp] = useState(openProp);
  if (openProp !== prevOpenProp) {
    setPrevOpenProp(openProp);
    if (prevOpenProp && !openProp && !leaving && !point && !prefersReducedMotion()) {
      setLeaving(true);
      setExitHold(true);
    }
  }
  useLayoutEffect(() => {
    if (!leaving || leavingRef.current) return;
    // Started by the parent (above), not close(): no callbacks, it already closed.
    leavingRef.current = true;
    exitWasAnchoredRef.current = false;
    pendingCloseRef.current = false;
    armExitTimer();
  }, [leaving]);

  // Point menu closed by its parent: same exit as a scrim tap; reopen cancels.
  useLayoutEffect(() => {
    if (dismissed) {
      if (!leavingRef.current) close();
    } else if (leavingRef.current) {
      cancelExit();
    }
  }, [dismissed]);

  useEffect(() => () => window.clearTimeout(exitTimerRef.current), []);

  // Trigger menu reopened while leaving.
  useEffect(() => {
    if (open && leavingRef.current) cancelExit();
  }, [open]);

  // Point menu: a new right-click / long-press (new point) while leaving.
  const pointXKey = point?.x;
  const pointYKey = point?.y;
  useEffect(() => {
    if (!leavingRef.current || pointXKey === undefined || pointYKey === undefined) return;
    const last = closedPointRef.current;
    if (last && last.x === pointXKey && last.y === pointYKey) return;
    cancelExit();
  }, [pointXKey, pointYKey]);

  useLayoutEffect(() => {
    if (!visible || leaving) return;
    armIgnoreDismiss();
  }, [visible, leaving]);

  // Desktop / phone clamp: card veil. Phone edge sheet uses the dialog scrim only (no double dim).
  useStudioMenuBlur(veil && visible && !leaving && !edgeSheet);

  useEffect(() => {
    if (!visible) setPage([]);
  }, [visible]);

  // Long-press often leaves a native text range; clear it so the sheet
  // does not open with labels highlighted. iOS can re-apply after mount.
  useLayoutEffect(() => {
    if (!visible) return;
    const clear = () => {
      const sel = window.getSelection?.();
      // Only a real highlight: removing a collapsed caret (the composer's)
      // blurred the editor and cost ~25ms on every menu open.
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) sel.removeAllRanges();
    };
    clear();
    const raf = window.requestAnimationFrame(clear);
    return () => window.cancelAnimationFrame(raf);
  }, [visible]);

  // Phone: the blur covers only the live page card (studioMenuVeilCard, never
  // the boot-frame ghost), matching its rect and radius. Vars go straight onto
  // the dialog: no extra render on open. Re-measured whenever the card or the
  // visual viewport moves (URL bar, keyboard, head gap settling).
  useLayoutEffect(() => {
    if (!edgeSheet || !visible) return;
    const layer = layerRef.current;
    if (!layer) return;
    const measure = () => {
      const card = studioMenuVeilCard();
      const r = card?.getBoundingClientRect();
      if (!card || !r || r.width < 1 || r.height < 1) {
        for (const name of ["top", "left", "width", "height", "radius"]) {
          layer.style.removeProperty(`--studio-action-menu-veil-${name}`);
        }
        return;
      }
      let bottom = r.bottom;
      // A phone nav floating inside the card stays sharp.
      card.querySelectorAll<HTMLElement>(".studio-mobile-bottom-nav").forEach((nav) => {
        const n = nav.getBoundingClientRect();
        if (n.height > 0 && n.top > r.top + r.height / 2) bottom = Math.min(bottom, n.top);
      });
      layer.style.setProperty("--studio-action-menu-veil-top", `${Math.round(r.top)}px`);
      layer.style.setProperty("--studio-action-menu-veil-left", `${Math.round(r.left)}px`);
      layer.style.setProperty("--studio-action-menu-veil-width", `${Math.round(r.width)}px`);
      layer.style.setProperty(
        "--studio-action-menu-veil-height",
        `${Math.max(0, Math.round(bottom - r.top))}px`,
      );
      layer.style.setProperty(
        "--studio-action-menu-veil-radius",
        getComputedStyle(card).borderTopLeftRadius || "0px",
      );
    };
    measure();
    // One more pass after showModal / first paint settles the layout.
    const raf = window.requestAnimationFrame(measure);
    const vv = window.visualViewport;
    const card = studioMenuVeilCard();
    const ro =
      card && typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (card) ro?.observe(card);
    window.addEventListener("resize", measure);
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    return () => {
      window.cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, [edgeSheet, visible]);

  // Mobile: showModal in the same layout pass as mount so the slide starts
  // on the first painted frame (blur is a top-layer scrim, not a page filter).
  useLayoutEffect(() => {
    if (!edgeSheet || !visible || leaving) return;
    const el = layerRef.current;
    if (el && !el.open) el.showModal();
  }, [edgeSheet, visible, leaving]);

  // Desktop mounts hidden at 0,0 on the opening render itself (MENU_HIDDEN_STYLE);
  // placement below moves it in the same layout pass, before first paint. This
  // used to be an extra setState pass (open → hidden style → placed = 3 renders).
  useLayoutEffect(() => {
    if (!visible) setMenuStyle(null);
  }, [visible]);

  const pointX = point?.x;
  const pointY = point?.y;
  useLayoutEffect(() => {
    if (!visible || edgeSheet) return;
    const menu = menuRef.current;
    if (!menu) return;
    const pad = 8;
    const place = () => {
      const { width, height } = menu.getBoundingClientRect();
      let anchor: FloatingRect | null = anchorRect ?? null;
      if (!anchor && (pointX === undefined || pointY === undefined)) {
        const wrap = wrapRef.current;
        if (!wrap) return;
        anchor = wrap.getBoundingClientRect();
      }
      const placed = placeFloating({
        width,
        height,
        anchor,
        point: anchor ? null : { x: pointX as number, y: pointY as number },
        placement,
        // Own-trigger menus (ellipsis buttons) have always aligned to the trigger end.
        align: anchorRect ? anchorAlign : anchor ? "end" : "start",
        pad,
      });
      const { left, top, maxWidth: maxW, maxHeight: maxH } = placed;
      const nextTop = `${Math.round(top)}px`;
      const nextLeft = `${Math.round(left)}px`;
      const nextMax = `${Math.round(maxW)}px`;
      const nextMaxH = `${Math.round(maxH)}px`;
      setMenuStyle((current) =>
        current &&
        current.top === nextTop &&
        current.left === nextLeft &&
        current.maxWidth === nextMax &&
        current.maxHeight === nextMaxH &&
        !current.visibility
          ? current
          : {
              position: "fixed",
              top: nextTop,
              left: nextLeft,
              maxWidth: nextMax,
              maxHeight: nextMaxH,
              overflowY: "auto",
              overflowX: "hidden",
            },
      );
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(menu);
    window.addEventListener("resize", place);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
    };
  }, [
    visible,
    edgeSheet,
    pointX,
    pointY,
    placement,
    page,
    items.length,
    anchorRect?.left,
    anchorRect?.right,
    anchorRect?.top,
    anchorRect?.bottom,
    anchorAlign,
  ]);

  useEffect(() => {
    if (!visible) return;
    const swallowFollowingClick = () => {
      const swallow = (click: Event) => {
        click.preventDefault();
        click.stopPropagation();
      };
      document.addEventListener("click", swallow, true);
      window.setTimeout(() => document.removeEventListener("click", swallow, true), 0);
    };
    /** The click after this press must not toggle the dropdown a second time. */
    const swallowToggleClick = () => {
      const done = () => {
        document.removeEventListener("click", swallow, true);
        document.removeEventListener("pointerup", onUp, true);
        document.removeEventListener("pointercancel", onUp, true);
      };
      const swallow = (click: Event) => {
        click.preventDefault();
        click.stopPropagation();
        done();
      };
      const onUp = () => window.setTimeout(done, 0);
      document.addEventListener("click", swallow, true);
      document.addEventListener("pointerup", onUp, true);
      document.addEventListener("pointercancel", onUp, true);
    };
    const onDoc = (event: PointerEvent) => {
      const target = event.target as Node;
      // A point dropdown's own toggle (it sits outside the menu). Its click
      // used to land ~100ms after the press, after the 0ms swallow above, and
      // flipped the parent's `open` mid-slide: the close was cut off.
      const ownToggle =
        anchored && target instanceof Element && target.closest(OPEN_TOGGLE_SELECTOR) !== null;
      if (leavingRef.current) {
        // Re-press on the toggle during the slide reopens it, like a trigger menu.
        if (ownToggle && pendingCloseRef.current) {
          event.preventDefault();
          event.stopPropagation();
          swallowToggleClick();
          cancelExit();
        }
        // Anything else: let the press reach whatever is under it.
        return;
      }
      if (
        wrapRef.current?.contains(target) ||
        menuRef.current?.contains(target) ||
        layerRef.current?.contains(target)
      ) {
        return;
      }
      // Phone: the opening press can land on the new scrim. Desktop: an
      // outside press always closes, and the click must not reach the row.
      if (shouldIgnoreDismiss() && edgeSheet) return;
      event.preventDefault();
      event.stopPropagation();
      if (ownToggle) swallowToggleClick();
      else swallowFollowingClick();
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (page.length) setPage((current) => current.slice(0, -1));
      else close();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onDoc, true);
      window.addEventListener("keydown", onKey, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onDoc, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [visible, edgeSheet, page, anchored]);

  // Phone keyboard (inline fields in the menu): track the visible band so the
  // menu lifts above the keyboard, same visualViewport math as the shell.
  const [keyboardBand, setKeyboardBand] = useState<{ top: number; height: number } | null>(
    null,
  );
  useEffect(() => {
    if (!visible || !edgeSheet || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const sync = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setKeyboardBand((prev) => {
          if (inset < 24) return prev ? null : prev;
          const next = { top: Math.round(vv.offsetTop), height: Math.round(vv.height) };
          return prev && prev.top === next.top && prev.height === next.height ? prev : next;
        });
      });
    };
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    sync();
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      setKeyboardBand(null);
    };
  }, [visible, edgeSheet]);

  // Sub-page title names the list you are in ("Ratio"), not "Back".
  const pageTitle = (() => {
    let current = items;
    let name: string | null = null;
    for (const id of page) {
      name = pageLabel(current, id);
      current = pageChildren(current, id);
    }
    return name;
  })();
  const title = page.length ? (pageTitle ?? "Back") : label;
  // A page whose first item draws its own Back / Close skips the sheet head.
  const pageOwnsNav = (() => {
    if (!page.length) return false;
    let current = items;
    for (const id of page) current = pageChildren(current, id);
    return Boolean(current[0]?.ownsNav);
  })();
  const submenuCaret =
    typeof menuClassName === "string" && menuClassName.includes("studio-dm-context-menu")
      ? "dot"
      : "arrow";
  const body = (
    <MenuPages
      items={items}
      page={page}
      submenuCaret={submenuCaret}
      showChecks={showChecks}
      onOpen={(id) => setPage((current) => [...current, id])}
      onBack={() => setPage((current) => current.slice(0, -1))}
      onDone={() => close()}
    />
  );

  const dockStart = edgeSheet && dock === "left";
  const keyboardOpen = Boolean(edgeSheet && keyboardBand);
  const menuClass = `studio-action-menu${!edgeSheet && menuWidth ? " has-fixed-width" : ""}${!edgeSheet && menuMinWidth ? " has-min-width" : ""}${edgeSheet ? " is-edge" : ""}${keyboardOpen ? " is-keyboard-open" : ""}${dockStart ? " is-start" : ""}${showChecks ? " has-checks" : ""}${menuClassName ? ` ${menuClassName}` : ""}`;
  const menuNode = (
    <div
      ref={menuRef}
      className={`cursor-tab-context-menu studio-add-menu ${menuClass} is-fixed${leaving ? " is-leaving" : ""}`}
      style={
        edgeSheet
          ? keyboardBand
            ? ({
                "--studio-action-menu-vv-top": `${keyboardBand.top}px`,
                "--studio-action-menu-vv-h": `${keyboardBand.height}px`,
              } as CSSProperties)
            : undefined
          : menuWidth || menuMinWidth
            ? ({
                ...(menuStyle ?? MENU_HIDDEN_STYLE),
                ...(menuWidth ? { "--studio-action-menu-w": `${Math.round(menuWidth)}px` } : {}),
                ...(menuMinWidth ? { "--studio-action-menu-min-w": `${Math.round(menuMinWidth)}px` } : {}),
              } as CSSProperties)
            : menuStyle ?? MENU_HIDDEN_STYLE
      }
      role="menu"
      onPointerDown={(event) => event.stopPropagation()}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget && leavingRef.current) finishExit();
      }}
    >
      {edgeSheet ? (
        <span className="studio-action-menu-title-sizer" aria-hidden="true">
          {title}
        </span>
      ) : null}
      {edgeSheet && !pageOwnsNav ? (
        <div className={`studio-action-menu-head${head ? " has-extra" : ""}`}>
          <div className="studio-action-menu-title-row">
            <span className="studio-action-menu-title">{title}</span>
            <button
              type="button"
              className="studio-action-menu-close"
              aria-label="Close"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                close();
              }}
            >
              <X aria-hidden="true" />
            </button>
          </div>
          {head ? <div className="studio-action-menu-extra">{head}</div> : null}
        </div>
      ) : null}
      <div
        className={edgeSheet ? "studio-action-menu-scroll" : undefined}
        {...(edgeSheet ? menuGestureHandlers : null)}
      >
        {lead}
        {body}
      </div>
    </div>
  );
  // Mounts on the opening render (phone and desktop): no wait for a style pass.
  const panel = visible ? (
    <StudioPortalLayer open title={title} attach="body" onClose={close}>
      {edgeSheet ? (
        <dialog
          ref={layerRef}
          className={`studio-action-menu-layer${dockStart ? " is-start" : ""}${leaving ? " is-leaving" : ""}`}
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
          onClick={(event) => {
            if (shouldIgnoreDismiss()) return;
            if (event.target !== event.currentTarget) return;
            if (menuGestureWasScroll()) return;
            close();
          }}
        >
          <span className="studio-action-menu-veil" aria-hidden="true" />
          <button
            type="button"
            className="studio-action-menu-scrim"
            aria-label="Dismiss"
            tabIndex={-1}
            onPointerUp={(event) => {
              // Swallow the opening gesture's pointerup on the new scrim.
              if (shouldIgnoreDismiss()) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (shouldIgnoreDismiss()) return;
              if (menuGestureWasScroll()) return;
              close();
            }}
          />
          {menuNode}
        </dialog>
      ) : (
        menuNode
      )}
    </StudioPortalLayer>
  ) : null;

  if (anchored) return panel;

  const coarseTitle = useCoarsePointer();
  const toggleOpen = () => {
    if (open) close();
    else setOpen(true);
  };

  return (
    <div className="relative studio-file-add-wrap" ref={wrapRef}>
      <button
        type="button"
        className={
          triggerClassName ||
          (trigger
            ? undefined
            : "studio-settings-pill studio-settings-trigger studio-file-add-toggle studio-menu-circle-btn")
        }
        title={triggerTitle && !coarseTitle ? triggerTitle : undefined}
        aria-label={label}
        aria-haspopup={edgeSheet ? "dialog" : "menu"}
        aria-expanded={open}
        onPointerDown={(event) => {
          event.stopPropagation();
          pressToggledRef.current = false;
          // Mouse opens on press (saves the ~100ms mousedown→mouseup wait).
          // Touch/pen stay on click: a press-open fires on scroll starts and
          // mounts the phone overlay under a still-down finger.
          if (event.pointerType !== "mouse" || event.button !== 0) return;
          if (edgeSheet) return;
          pressToggledRef.current = true;
          toggleOpen();
        }}
        onClick={(event) => {
          event.stopPropagation();
          // The press already toggled; keyboard clicks (detail 0) always toggle.
          if (pressToggledRef.current && event.detail !== 0) {
            pressToggledRef.current = false;
            return;
          }
          pressToggledRef.current = false;
          toggleOpen();
        }}
      >
        {trigger ?? <Ellipsis className="h-3.5 w-3.5" />}
      </button>
      {panel}
    </div>
  );
}
